import { useTranslation } from "react-i18next";

import moment from "moment";
import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { DialogProps } from "components/Actions/types";
import DatePicker from "components/DatePicker";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import {
    TOOLTIP_PLACEMENT,
    TooltipLarge
} from "components/DesignComponents/Tooltips";
import HighlightedOption from "components/HighlightedOption";
import {
    disableDates,
    DROPDOWN_SIZES,
    maxDialogWidth,
    useMediaQueries
} from "shared";

import DialogActions from "../../components/DialogActions";
import { useCompanyLevelManualChange } from "./hooks";
import RevertLevel from "./RevertLevel";
import {
    ADDITIONAL_DATA,
    LEVEL_TYPES,
    LOADERS,
    TIME_PERIODS,
    TimePeriodOption
} from "./types";

const CompanyLevelManualChange = ({ isOpen, close }: DialogProps) => {
    const { textPlaceholder } = colors;

    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();

    const {
        isLoading,
        levelInputValue,
        level,
        defaultLevel,
        validUntil,
        hasValidUntilError,
        levelType,
        rootCompany,
        rootCompanyLevel,
        timePeriod,
        isCustomTimePeriod,
        clientEmail,
        salesEmail,
        explanation,
        isClientEmailValid,
        isSalesEmailValid,
        clientEmailError,
        salesEmailError,
        MAX_EXPIRATION_DAYS,
        validateClientEmail,
        validateSalesEmail,
        handleCloseDialog,
        handleSubmit,
        resetData,
        getLevelOptions,
        isLevelOptionDisabled,
        setLevelInputValue,
        setLevel,
        getTimePeriodOptions,
        setTimePeriod,
        setValidUntil,
        handleEndDateChange,
        handleEndDateError,
        handleChangeAdditionalData
    } = useCompanyLevelManualChange(isOpen, close);

    return (
        <Dialog
            title={t("General##company level")}
            description={t("Dialog##company level change description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => resetData(true)
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading.submit}
                    isReadyToConfirm={
                        level &&
                        validUntil &&
                        !hasValidUntilError &&
                        (clientEmail.length ? isClientEmailValid : true) &&
                        (salesEmail.length ? isSalesEmailValid : true)
                    }
                    confirmationButtonText={t("Button##save")}
                />
            }
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            <div
                css={css({
                    margin: "8px 0 16px 0",

                    "& > div + div": {
                        marginTop: "16px"
                    }
                })}
            >
                {levelType === LEVEL_TYPES.Manual && (
                    <RevertLevel
                        date={validUntil as string}
                        defaultLevel={defaultLevel}
                        resetData={resetData}
                    />
                )}

                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Medium}
                    labelLeft={t("Dialog##company")}
                    value={rootCompany?.name}
                    readOnly
                />

                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Medium}
                    labelLeft={t("Dialog##company level type")}
                    isLoading={isLoading[LOADERS.LevelType]}
                    value={
                        isLoading[LOADERS.LevelType]
                            ? ""
                            : t(`General##${levelType}`)
                    }
                    readOnly
                />

                <Autocomplete
                    labelLeft={t("General##company level")}
                    fullWidth
                    isRequired
                    readOnly={isLoading.submit}
                    value={level}
                    size={DROPDOWN_SIZES.Medium}
                    options={getLevelOptions()}
                    getOptionDisabled={option => isLevelOptionDisabled(option)}
                    onInputChange={(_, inputValue) =>
                        setLevelInputValue(inputValue)
                    }
                    onClose={() => setLevelInputValue("")}
                    onChange={(_, val) => setLevel(val)}
                    renderOption={(props, value) => {
                        const { key: _key, ...restProps } = props;

                        return (
                            <TooltipLarge
                                key={value}
                                title={
                                    isLevelOptionDisabled(value)
                                        ? t(
                                              "Dialog##downgrading company level is not possible"
                                          )
                                        : ""
                                }
                                placement={TOOLTIP_PLACEMENT.Top}
                            >
                                <li>
                                    <span {...restProps}>
                                        <HighlightedOption
                                            text={value}
                                            query={levelInputValue}
                                        />

                                        {value === rootCompanyLevel && (
                                            <span
                                                css={css({
                                                    color: textPlaceholder,
                                                    marginLeft: "5px"
                                                })}
                                            >
                                                ({t("Dialog##current level")})
                                            </span>
                                        )}
                                    </span>
                                </li>
                            </TooltipLarge>
                        );
                    }}
                />

                <Autocomplete
                    labelLeft={t("Dialog##time period")}
                    textFieldParams={{
                        placeholder: t("Dialog##select")
                    }}
                    fullWidth
                    isRequired
                    readOnly={isLoading.submit}
                    loading={isLoading[LOADERS.LevelType]}
                    value={timePeriod}
                    size={DROPDOWN_SIZES.Medium}
                    options={
                        isLoading[LOADERS.LevelType]
                            ? []
                            : getTimePeriodOptions()
                    }
                    getOptionLabel={option => option.value}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    onChange={(_: any, val: TimePeriodOption | null) => {
                        setTimePeriod(val);

                        const validUntilValue =
                            val?.id === TIME_PERIODS.January31NextYear
                                ? val.value
                                : null;

                        setValidUntil(validUntilValue);
                    }}
                />

                <DatePicker
                    label={t("Dialog##valid until")}
                    disablePast
                    value={validUntil ? moment.utc(validUntil) : null}
                    shouldDisableYear={date =>
                        disableDates(date, MAX_EXPIRATION_DAYS, "days")
                    }
                    shouldDisableMonth={date =>
                        disableDates(date, MAX_EXPIRATION_DAYS, "days")
                    }
                    shouldDisableDate={date =>
                        disableDates(date, MAX_EXPIRATION_DAYS, "days")
                    }
                    readOnly={!isCustomTimePeriod || isLoading.submit}
                    onChange={handleEndDateChange}
                    getError={handleEndDateError}
                    textFieldParams={{
                        required: true,
                        tooltipProps: {
                            title: !isCustomTimePeriod
                                ? t(
                                      "Dialog##valid until date disable description"
                                  )
                                : "",
                            isInteractiveDisabled: true
                        },
                        timeZoneExplanatory: "(UTC+00:00)"
                    }}
                />

                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Medium}
                    labelLeft={t("Dialog##client email")}
                    placeholder={t("General##enter here")}
                    isLoading={isLoading[LOADERS.LevelType]}
                    readOnly={isLoading.submit}
                    value={clientEmail}
                    onBlur={validateClientEmail}
                    errorText={isClientEmailValid ? "" : clientEmailError}
                    name={ADDITIONAL_DATA.ClientEmail}
                    onChange={handleChangeAdditionalData}
                />

                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Medium}
                    labelLeft={t("Dialog##sales email")}
                    placeholder={t("General##enter here")}
                    isLoading={isLoading[LOADERS.LevelType]}
                    readOnly={isLoading.submit}
                    value={salesEmail}
                    onBlur={validateSalesEmail}
                    errorText={isSalesEmailValid ? "" : salesEmailError}
                    name={ADDITIONAL_DATA.SalesEmail}
                    onChange={handleChangeAdditionalData}
                />

                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Medium}
                    labelLeft={t("Dialog##explanation")}
                    placeholder={t("General##enter here")}
                    isLoading={isLoading[LOADERS.LevelType]}
                    readOnly={isLoading.submit}
                    value={explanation}
                    name={ADDITIONAL_DATA.Explanation}
                    onChange={handleChangeAdditionalData}
                />
            </div>
        </Dialog>
    );
};

export default CompanyLevelManualChange;
